import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  InfoTooltip,
  Dropdown,
  DropdownToggleType,
  DownloadDropdown,
  DatePicker,
  ToggleSwitch,
} from "../../Components";
import WidgetContainer from "../../Components/WidgetContainer";
import "./InvestmentPerformance.scss";
import * as R from "ramda";
import { currencyFormatter } from "../../MMM/MMMUtils";
import { SnapshotChart } from "./Components/SnapshotChart";
import { ROI, AttributionOverTime } from "@blisspointmedia/bpm-types/dist/GeoMMM";
import ChartContainer from "../../Components/ChartContainer";
import {
  DateGroupingOption,
  getDefaultDates,
} from "../../MMM/ModelResults/ChannelPlatformTacticBrand";
import {
  formattedSnapShotData,
  formattedAreaData,
  snapShotDataSort,
  OUTCOME_TYPE_STRING,
  OUTCOME_FORMATTER_TYPE,
} from "../DataFormatters";
import { formatMoney } from "../../utils/format-utils";
import { useCompanyInfo } from "../../redux/company";
import { dateToMddyy } from "../../utils/date-utils";
import { OutcomeAndResponseChart } from "./Components/OutcomeAndResponseChart";
import StackedAreaChart from "../../MMM/ModelResults/StackedAreaChart";
import { downloadPNG, exportToExcel } from "../../utils/download-utils";

const HARD_CODED_COLOR = "#cbd2e1";
const HARD_CODED_COLOR_NAME = "Baseline";

const DEFAULT_SORT_SELECTIONS = {
  spendAndEffectShare: "Spend: High to low",
  kpiVolume: "Highest to lowest",
  revenue: "Highest to lowest",
  costPerAcquisition: "Highest to lowest",
  roas: "Highest to lowest",
};

const WIDGET_SUBHEADER = {
  kpiVolume: (
    <>
      Attribution of kpi volume to each investment based on model parameter estimates.
      <InfoTooltip rightLabel="More info" size="sm">
        Calculated for each point in time as (level of media investment at that point in time) x
        (model parameter estimates for that media input).
      </InfoTooltip>
    </>
  ),
  revenue: (
    <>
      Attribution of revenue to each investment based on model parameter estimates.
      <InfoTooltip rightLabel="More info" size="sm">
        Calculated for each point in time as (level of media investment at that point in time) x
        (model parameter estimates for that media input).
      </InfoTooltip>
    </>
  ),
  costPerAcquisition: (
    <>
      Attribution of cost-pers to each investment based on model parameter estimates.
      <InfoTooltip rightLabel="More info" size="sm">
        Calculated for each point in time as (level of media investment at that point in time) /
        ((level of media investment at that point in time) x (model parameter estimates for that
        media input)).
      </InfoTooltip>
    </>
  ),
  roas: (
    <>
      Attribution of ROAS to each investment based on model parameter estimates.
      <InfoTooltip rightLabel="More info" size="sm">
        Calculated for each point in time as ((level of media investment at that point in time) x
        (model parameter estimates for that media input)) / (level of media investment at that point
        in time).
      </InfoTooltip>
    </>
  ),
  spendAndEffectShare: (
    <>
      Attribution of spend and effect share to each investment based on model parameter estimates.
      <InfoTooltip rightLabel="More info" size="sm">
        Effect share calculated for each point in time as ((level of media investment at that point
        in time) x (model parameter estimates for that media input)) / (total KPI volume).
      </InfoTooltip>
    </>
  ),
};

const DEFAULT_SORT_OPTIONS = [
  { value: "Alphabetical" },
  { value: "Highest to lowest" },
  { value: "Lowest to highest" },
];

export const DATE_GROUPING_OPTIONS: DateGroupingOption[] = [
  { value: "Week", label: "Weekly" },
  { value: "Month", label: "Monthly" },
  { value: "Quarter", label: "Quarterly" },
];

const SNAPCHART_TITLE = {
  spendAndEffectShare: "Spend and Effect Share",
  kpiVolume: "KPI Volume Decomposition Share",
  revenue: "Revenue Decomposition Share",
  costPerAcquisition: "Average CPA",
  roas: "Average ROAS",
};

interface InvestmentPerformanceProps {
  groupBy: string;
  outcomeVariable: "revenue" | "kpiVolume";
  attributionOverTimeRows: any[];
  channelBreakoutMap: Record<
    string,
    {
      channel: string;
      platform: string;
      tactic: string;
      brand_nonbrand: string;
    }
  >;
}

export const InvestmentPerformance: React.FC<InvestmentPerformanceProps> = ({
  groupBy,
  outcomeVariable,
  attributionOverTimeRows,
  channelBreakoutMap,
}) => {
  const [investment, setInvestment] = useState<string>(outcomeVariable);
  const [investmentOptions, setInvestmentOptions] = useState<
    { label: string; value: string; title: string }[]
  >([]);
  const [snapChartSortValue, setSnapChartSortValue] = useState("Highest to lowest");
  const [toggleLabel, setToggleLabel] = useState(false);
  const [absoluteOrRelative, setAbsoluteOrRelative] = useState("Absolute");
  const [dateGrouping, setDateGrouping] = useState("Week");
  const [dates, setDates] = useState({ start: "", end: "" });
  const [covariates, setCovariates] = useState(false);
  const [logScale, setLogScale] = useState(false);
  const [areaChartData, setAreaChartData] = useState<any>({});
  const [areaChartData2, setAreaChartData2] = useState<any>({});
  const [snapChartData, setSnapChartData] = useState<any>([]);
  const { fiscal_cal: fiscalCal } = useCompanyInfo();

  const SPEND_AND_EFFECT_SORT_OPTIONS = [
    { value: "Alphabetical" },
    { value: "Spend to effect ratio" },
    { value: "Spend: High to low" },
    { value: "Spend: Low to high" },
    { value: `${OUTCOME_TYPE_STRING[outcomeVariable]}: High to low` },
    { value: `${OUTCOME_TYPE_STRING[outcomeVariable]}: Low to high` },
  ] as const;

  useEffect(() => {
    if (outcomeVariable) {
      setInvestment(outcomeVariable === "revenue" ? "revenue" : "kpiVolume");

      const isRevenue = outcomeVariable === "revenue";
      setInvestmentOptions([
        {
          label: OUTCOME_TYPE_STRING[outcomeVariable],
          value: outcomeVariable,
          title: `${OUTCOME_TYPE_STRING[outcomeVariable]} Decomposition`,
        },
        {
          label: isRevenue ? "ROAS" : "Cost Per Acquisition",
          value: isRevenue ? "roas" : "costPerAcquisition",
          title: isRevenue ? "ROAS" : "Cost Per Acquisition",
        },
        {
          label: "Spend and Effect Share",
          value: "spendAndEffectShare",
          title: "Spend and Effect",
        },
      ]);
    }
  }, [outcomeVariable]);

  const { data } = useMemo(() => {
    if (R.isEmpty(attributionOverTimeRows)) {
      return { roiData: [], attributionOverTimeData: [] };
    }

    // ATTRIBUTION OVER TIME DATA
    let attributionOverTimeDataMap: { [breakout: string]: AttributionOverTime } = {};
    attributionOverTimeRows.forEach(row => {
      const {
        channel: breakout,
        time: date,
        metric,
        incremental_outcome: incrementalOutcome,
        spend,
      } = row;
      const key = `${breakout.toLowerCase()}_${date}`;
      if (breakout === "Baseline") {
        attributionOverTimeDataMap[key] = {
          date,
          name: breakout,
          channel: "baseline",
          platform: null,
          tactic: null,
          brand_nonbrand: null,
          incrementalOutcome,
          incrementalOutcomeLower: incrementalOutcome,
          incrementalOutcomeUpper: incrementalOutcome,
          spend,
        };
        return;
      }
      if (channelBreakoutMap[breakout.toLowerCase()]) {
        const key = `${breakout.toLowerCase()}_${date}`;
        if (!attributionOverTimeDataMap[key]) {
          const { channel, platform, tactic, brand_nonbrand } = channelBreakoutMap[
            breakout.toLowerCase()
          ];

          attributionOverTimeDataMap[key] = {
            date,
            name: breakout,
            channel: channel?.replace(/[ -]/g, "_") ?? null,
            platform: platform?.replace(/[ -]/g, "_") ?? null,
            tactic: tactic?.replace(/[ -]/g, "_") ?? null,
            brand_nonbrand: brand_nonbrand?.replace(/[ -]/g, "_") ?? null,
            incrementalOutcome: 0,
            incrementalOutcomeLower: 0,
            incrementalOutcomeUpper: 0,
            spend,
          };
        }

        if (metric === "mean") {
          attributionOverTimeDataMap[key].incrementalOutcome = incrementalOutcome;
        } else if (metric === "ci_lo") {
          attributionOverTimeDataMap[key].incrementalOutcomeLower = incrementalOutcome;
        } else if (metric === "ci_hi") {
          attributionOverTimeDataMap[key].incrementalOutcomeUpper = incrementalOutcome;
        }
      }
    });

    return {
      data: R.values(attributionOverTimeDataMap),
    };
  }, [attributionOverTimeRows, channelBreakoutMap]);

  useEffect(() => {
    if (data && !R.isEmpty(data) && (dates.start === "" || dates.end === "")) {
      return setDates(getDefaultDates(data as any[]));
    }

    if (data && !R.isEmpty(data) && dates.start !== "" && dates.end !== "") {
      let dateRangeFilteredData = R.filter(item => {
        const itemDate = new Date((item as any).date);
        return itemDate >= new Date(dates.start) && itemDate <= new Date(dates.end);
      })(data);

      dateRangeFilteredData = !covariates
        ? R.filter(R.compose(R.not, R.propEq("name", "Baseline")))(dateRangeFilteredData)
        : dateRangeFilteredData;

      let chartData;
      switch (investment) {
        case "kpiVolume":
        case "revenue":
          chartData = formattedAreaData({
            data: dateRangeFilteredData,
            data1Key: "incrementalOutcome",
            groupBy,
            dateGrouping,
            relative: absoluteOrRelative === "Relative",
            fiscalCal,
          });
          break;
        case "costPerAcquisition":
          chartData = formattedAreaData({
            data: dateRangeFilteredData,
            data1Key: "spend",
            groupBy,
            dateGrouping,
            data2Key: "incrementalOutcome",
            fiscalCal,
          });
          break;
        case "roas":
          chartData = formattedAreaData({
            data: dateRangeFilteredData,
            data1Key: "incrementalOutcome",
            groupBy,
            dateGrouping,
            data2Key: "spend",
            fiscalCal,
          });
          break;
        default: {
          // spend and effect
          chartData = formattedAreaData({
            data: dateRangeFilteredData,
            data1Key: "spend",
            groupBy,
            dateGrouping,
            relative: absoluteOrRelative === "Relative",
            fiscalCal,
          });
          const chartData2 = formattedAreaData({
            data: dateRangeFilteredData,
            data1Key: "incrementalOutcome",
            groupBy,
            dateGrouping,
            relative: absoluteOrRelative === "Relative",
            fiscalCal,
          });

          setAreaChartData2(chartData2);
          break;
        }
      }

      setAreaChartData(chartData);

      // Iterate through data and use a map to group by date by aggregating the different values in the data
      const snapShotDataMap: { [key: string]: ROI } = {};
      dateRangeFilteredData.forEach(row => {
        const {
          name,
          channel,
          platform,
          tactic,
          brand_nonbrand,
          incrementalOutcome,
          incrementalOutcomeLower,
          incrementalOutcomeUpper,
          spend,
        } = row;
        if (!snapShotDataMap[name]) {
          snapShotDataMap[name] = {
            name,
            channel,
            platform,
            tactic,
            brand_nonbrand,
            incrementalOutcome: 0,
            incrementalOutcomeLower: 0,
            incrementalOutcomeUpper: 0,
            spend: 0,
          };
        }

        snapShotDataMap[name].incrementalOutcome += incrementalOutcome;
        snapShotDataMap[name].incrementalOutcomeLower += incrementalOutcomeLower;
        snapShotDataMap[name].incrementalOutcomeUpper += incrementalOutcomeUpper;
        snapShotDataMap[name].spend += spend;
      });

      const snapShotData = formattedSnapShotData({
        data: R.values(snapShotDataMap),
        groupBy,
        conversionTypeString: OUTCOME_TYPE_STRING[outcomeVariable],
        type: investment,
        outcomeVariable,
      });

      snapShotData.barData = snapShotDataSort(snapShotData.barData, snapChartSortValue);
      setSnapChartData(snapShotData);
    }
  }, [
    absoluteOrRelative,
    attributionOverTimeRows,
    covariates,
    dateGrouping,
    dates,
    groupBy,
    investment,
    snapChartSortValue,
    fiscalCal,
    data,
    outcomeVariable,
    logScale,
  ]);

  const revenueAbsoluteRelativeFormatter = useCallback(
    val => {
      if (absoluteOrRelative === "Absolute") {
        return OUTCOME_FORMATTER_TYPE[outcomeVariable].format(val, 2);
      } else {
        return absoluteOrRelative === "Relative" ? `${val.toFixed(2)}%` : "";
      }
    },
    [absoluteOrRelative, outcomeVariable]
  );

  const spendAbsoluteRelativeFormatter = useCallback(
    val => {
      if (absoluteOrRelative === "Absolute") {
        return val < 1000 ? currencyFormatter.format(val, 0) : currencyFormatter.format(val, 1);
      } else {
        return absoluteOrRelative === "Relative" ? `${val.toFixed(0)}%` : "";
      }
    },
    [absoluteOrRelative]
  );

  const buildSnapshotHeader = useCallback(() => {
    switch (investment) {
      case "kpiVolume":
      case "revenue":
        return `Total ${OUTCOME_TYPE_STRING[investment]}: ${snapChartData.titleData}`;
      case "costPerAcquisition":
      case "roas":
        return `Overall ${investment === "roas" ? "ROAS" : "CPA"}: ${snapChartData.titleData}`;
      default:
        return undefined;
    }
  }, [investment, snapChartData]);

  const snapChartFormatter = useCallback(
    value => {
      switch (investment) {
        case "costPerAcquisition":
          return value === null ? "--" : formatMoney(value);
        case "roas":
          return value === null ? "--" : `${value.toFixed(2)}`;
        case "revenue":
        default:
          return value === null ? "--" : `${value.toFixed(2)}%`;
      }
    },
    [investment]
  );

  const xAxisTickFormatter = useCallback(
    // this isn't doing anything now, we should agree on how we display dates by grouping
    // and then move to a util and change it everywhere
    (val: string) => {
      return dateGrouping === "Week" ? dateToMddyy(val) : dateToMddyy(val);
    },
    [dateGrouping]
  );
  const [activeArea, setActiveArea] = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const rightChart = useMemo(() => {
    return investment !== "spendAndEffectShare" ? (
      <OutcomeAndResponseChart
        areas={areaChartData.areas}
        activeArea={activeArea}
        setActiveArea={setActiveArea}
        activeGroup={activeGroup}
        setActiveGroup={setActiveGroup}
        data={areaChartData.newD}
        dateGrouping={dateGrouping}
        hardCodedColorName={HARD_CODED_COLOR_NAME}
        hardCodedColor={HARD_CODED_COLOR}
        groupedAreas={areaChartData.groupedAreas}
        tooltipFormatter={val => {
          if (investment === "revenue" || investment === "kpiVolume") {
            return revenueAbsoluteRelativeFormatter(val);
          } else if (investment === "costPerAcquisition") {
            if (!val) {
              return val;
            }

            return val > 15
              ? `${currencyFormatter.format(val, 0)}`
              : `${currencyFormatter.format(val, 2)}`;
          } else if (investment === "roas") {
            return val ? `${val.toFixed(2)}` : val;
          }
        }}
        usePercentageYAxis={absoluteOrRelative === "Relative"}
        xAxisDataKey="date"
        xAxisTickFormatter={val => xAxisTickFormatter(val)}
        yAxisTickFormatter={
          investment === "roas"
            ? undefined
            : investment === "costPerAcquisition"
            ? val => `${currencyFormatter.format(val, 2)}`
            : val => `${OUTCOME_FORMATTER_TYPE[outcomeVariable].format(val, 2)}`
        }
        groupBy={groupBy}
        isResponse={investment === "roas" || investment === "costPerAcquisition"} // Switches to line chart instead of area chart
        logScale={logScale}
        setLogScale={setLogScale}
      />
    ) : (
      <div className="cptb" style={{ display: "flex" }}>
        <StackedAreaChart
          areas={areaChartData.areas}
          chart1Title="Spend"
          chart2Title={`Effect (${OUTCOME_TYPE_STRING[outcomeVariable]})`}
          data1={areaChartData.newD}
          data2={areaChartData2.newD}
          dateGrouping={dateGrouping}
          groupedAreas={areaChartData.groupedAreas}
          hardCodedColorName={HARD_CODED_COLOR_NAME}
          hardCodedColor={HARD_CODED_COLOR}
          xAxisDataKey="date"
          tooltipFormatterTop={val => spendAbsoluteRelativeFormatter(val)}
          tooltipFormatterBottom={val => revenueAbsoluteRelativeFormatter(val)}
          usePercentageYAxis={absoluteOrRelative === "Relative"}
          xAxisTickFormatter={val => xAxisTickFormatter(val)}
          yAxisTickFormatter1={val => `${currencyFormatter.format(val, 0)}`}
          yAxisTickFormatter2={val => `${OUTCOME_FORMATTER_TYPE[outcomeVariable].format(val, 0)}`}
        />
      </div>
    );
  }, [
    investment,
    areaChartData,
    activeArea,
    activeGroup,
    dateGrouping,
    absoluteOrRelative,
    groupBy,
    logScale,
    outcomeVariable,
    areaChartData2.newD,
    revenueAbsoluteRelativeFormatter,
    xAxisTickFormatter,
    spendAbsoluteRelativeFormatter,
  ]);

  const rightChartTitle = useMemo(() => {
    const option = investmentOptions.find(item => item.value === investment);
    return option?.title;
  }, [investmentOptions, investment]);

  const excelDownloadRight = useCallback(() => {
    exportToExcel(data || [], "Attribution_Over_Time_Data");
  }, [data]);

  const pngDownloadRight = useCallback(async () => {
    await downloadPNG(".investmentPerformanceContainer .ipRight", `${rightChartTitle}`);
  }, [rightChartTitle]);

  return (
    <div className="investmentPerformanceWidget">
      <WidgetContainer
        collapsible
        header={
          <div className="ipHeader">
            <span>How well did our investments do based on </span>
            <Dropdown
              className="largeTitle"
              onChange={selection => {
                setSnapChartSortValue(DEFAULT_SORT_SELECTIONS[selection]);
                setInvestment(selection);
                if (selection === "roas" || selection === "costPerAcquisition") {
                  setAbsoluteOrRelative("Absolute");
                }
              }}
              options={investmentOptions}
              type={DropdownToggleType.WIDGET_TITLE}
              value={investment}
            />
            <span>?</span>
          </div>
        }
        rightActions={
          <Fragment>
            <div className="ipCovariatesToggle">
              <ToggleSwitch
                design="primary"
                size="lg"
                label="Covariates"
                checked={covariates}
                onChange={() => setCovariates(current => !current)}
                infoTooltipText="An independent variable that can influence the outcome of a given statistical trial, but which is not of direct interest."
              />
            </div>
            <DatePicker
              range={dates}
              light={true}
              isOutsideRange={date => {
                const range = getDefaultDates(data as any[]);
                return (
                  new Date(date) < new Date(range.start) || new Date(date) > new Date(range.end)
                );
              }}
              onChange={({ start, end }) => {
                setDates({
                  start: start,
                  end: end,
                });
              }}
            />
          </Fragment>
        }
        subHeader={WIDGET_SUBHEADER[investment]}
      >
        <div className="investmentPerformanceContainer">
          <div className="ipLeft">
            {!R.isEmpty(snapChartData) && (
              <SnapshotChart
                colorOverrides={{ Baseline: "#cbd2e1" }}
                data={snapChartData.barData}
                dropdownOptions={
                  investment === "spendAndEffectShare"
                    ? SPEND_AND_EFFECT_SORT_OPTIONS
                    : DEFAULT_SORT_OPTIONS
                }
                dropdownValue={snapChartSortValue}
                setDropdownValue={setSnapChartSortValue}
                doubleBarChart={investment === "spendAndEffectShare"}
                header={buildSnapshotHeader()}
                setToggleLabel={
                  investment !== "roas" && investment !== "costPerAcquisition"
                    ? setToggleLabel
                    : undefined
                }
                title={SNAPCHART_TITLE[investment]}
                toggleLabel={
                  investment !== "roas" && investment !== "costPerAcquisition" ? toggleLabel : false
                }
                toggleSwitchLabel={outcomeVariable === "kpiVolume" ? "Volume" : "$"}
                valueFormatter={snapChartFormatter}
                showConfidenceIntervals={investment !== "spendAndEffectShare"}
                enableTooltip={investment !== "spendAndEffectShare"}
                pngDownloadClassName=".investmentPerformanceContainer .ipLeft"
              />
            )}
          </div>
          <div className="ipRight">
            <ChartContainer
              enableHoverDesign
              height={405}
              title={
                <>
                  <Dropdown
                    type={DropdownToggleType.WIDGET_TITLE}
                    value={dateGrouping}
                    options={DATE_GROUPING_OPTIONS}
                    onChange={selection => setDateGrouping(selection)}
                  />
                  <div>{rightChartTitle}</div>
                </>
              }
              rightActions={
                <Fragment>
                  <div className="ipLogScaleToggle">
                    {/* <ToggleSwitch
                      design="secondary"
                      size="sm"
                      label="Log Scale"
                      checked={logScale}
                      onChange={() => setLogScale(current => !current)}
                      disabled={
                        absoluteOrRelative === "Relative" ||
                        investment === "spendAndEffectShare" ||
                        activeArea !== null ||
                        activeGroup !== null
                      }
                    /> */}
                  </div>
                  {(investment === "revenue" ||
                    investment === "kpiVolume" ||
                    investment === "spendAndEffectShare") && (
                    <Dropdown
                      type={DropdownToggleType.FILLED}
                      design="secondary"
                      size="sm"
                      value={absoluteOrRelative}
                      options={[{ value: "Absolute" }, { value: "Relative" }]}
                      onChange={option => {
                        if (option === "Relative") {
                          setLogScale(false);
                        }
                        setAbsoluteOrRelative(option);
                      }}
                    />
                  )}
                  <DownloadDropdown
                    size="sm"
                    onClickOptions={[excelDownloadRight, pngDownloadRight]}
                  />
                </Fragment>
              }
            >
              {!R.isEmpty(areaChartData) && rightChart}
            </ChartContainer>
          </div>
        </div>
      </WidgetContainer>
    </div>
  );
};

export default InvestmentPerformance;
